<template>
    <v-container v-if="$store.state.user">
        <v-row v-if="isStaff">
            <v-col>
                <!-- Title -->
                <div v-if="showTitle" :style="titleStyle" class="mb-2">
                    <span v-if="isCurrentBooking" class="font-weight-bold">{{ $translate("Current booking") }}</span>
                    <span v-else-if="nextBooking && displayRules !== 'current'" class="font-weight-bold">{{ $translate("Next booking") }}</span>
                    <span v-else-if="bookingChecked" class="font-weight-bold">{{ $translate(displayRules == 'current' ? "No booking" : "No bookings") }}</span>
                </div>

                <!-- Booking Information -->
                <div v-if="shouldShowBooking" :style="textStyle">
                    <span class="mb-2">{{ activityName }}, {{ isCurrentBooking ? nextBooking.start.hhmm() : Date.formatTimeRelative(nextBooking.start) }} - {{ nextBooking.end.hhmm() }}</span>
                    <br>
                    <span v-if="bookedByField">{{ $translate("Booked by") }}: {{ bookedBy }}</span>
                    <template v-if="questions && questions.length">
                        <div v-for="question in questions" :key="question.question">
                            <span v-if="question.prefix">{{ question.prefix }}:&nbsp;</span>{{ answers[question.question] }}
                        </div>
                    </template>
                </div>
            </v-col>
        </v-row>
        <v-row v-else>
            <v-col>
                <span class="font-weight-bold">{{ $translate('Only staff with permission can see this') }}</span>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
export default {
    name: "zoezi-current-and-next-booking",
    zoezi: {
        title: 'Current & next booking',
        icon: 'mdi-calendar-clock',
    },
    props: {
        selectedResource: {
            title: 'Resource',
            type: Number,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'name',
                value: 'id',
                getItems: () => {
                    return window.$zoeziapi.get('/api/resource/get').then((resources) => {
                        return resources.map(({ lastname, id }) => ({ name: lastname, id }));
                    });
                },
            },
            default: () => undefined,
        },
        displayRules: {
            title: 'Display rules',
            type: String,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'title',
                value: 'value',
                getItems: () => [
                    { title: 'Current booking', value: 'current' },
                    { title: 'Next booking', value: 'next' },
                    { title: 'Current & next booking', value: 'combined' },
                ]
            },
            default: () => 'combined',
        },
        showTitle: {
            title: 'Show title',
            type: Boolean,
            default: true,
        },
        textSize: {
            title: 'Text size',
            type: String,
            fulltype: 'csssize',
            default: () => '1rem',
        },
        bookedByField: {
            title: 'Show booked by',
            type: String,
            fulltype: 'select',
            typeinfo: {
                multiple: false,
                text: 'title',
                value: 'value',
                getItems: () => {
                    return window.$zoeziapi.get('/api/settings/fields/get').then((fields) => {
                        let formatedFields = fields.user.map(f => ({ title: f.title, value: f.name }));
                        formatedFields.push({ title: 'Name', value: 'name' });
                        return formatedFields;
                    });
                },
            },
            default: () => 'name',
        },
        questions: {
            title: 'Show questions',
            type: Array,
            fulltype: 'multitext',
            typeinfo: {
                config: [
                    {
                        id: 'prefix',
                        title: 'Prefix'
                    },
                    {
                        id: 'question',
                        title: 'Question'
                    }
                ]
            }
        }
    },
    data: () => ({
        bookingChecked: false,
        nextBooking: null,
        activityName: null,
        allocationSubscribe: null,
        rbSubscribe: null,
        nextBookingTimeouts: [],
        booker: null,
        now1s: Date.now(),
        nowUpdater: null
    }),
    watch: {
        selectedResource: {
            handler: function (val) {
                if (val) {
                    this.getNextAllocation();
                }
            },
            immediate: true,
        },
        displayRules() {
            this.clearBookingTimeout();
        }
    },
    computed: {
        titleStyle() {
            return {
                fontSize: `calc(${this.textSize} * 1.25)`,
            };
        },
        textStyle() {
            return {
                fontSize: this.textSize,
            };
        },
        isStaff() {
            return !!this.$store.state.user?.permissions?.includes('Show customers');
        },
        bookedBy() {
            if (this.nextBooking && !this.booker) {
                return this.$translate('Unknown');
            } else if (this.nextBooking && this.booker) {
                if (this.bookedByField in this.booker) {
                    return this.$translate(this.booker[this.bookedByField]);
                } else if (this.bookedByField in this.booker?.xf) { // Check if the field is in the extended fields
                    return this.$translate(this.booker.xf[this.bookedByField]);
                } else {
                    return this.$translate('Field not found');
                }
            } else {
                return this.$translate('No bookings');
            }
        },
        isCurrentBooking() {
            return this.nextBooking && this.nextBooking.start < this.now1s && this.nextBooking.end > this.now1s;
        },
        answers() {
            if (!this.nextBooking) {
                return {};
            }

            return this.questions.reduce((result, question) => {
                let answer = this.nextBooking.origin?.extendedInfo?.[question.question]
                if (Array.isArray(answer)) {
                    answer = answer.join(', ');
                } else if (typeof answer === 'boolean') {
                    answer = this.$translate(answer ? 'Yes' : 'No');
                }
                result[question.question] = answer || '';
                return result;
            }, {});
        },
        shouldShowBooking() {
            if (!this.nextBooking) {
                return false;
            }
            if (this.displayRules === 'current') {
                return this.isCurrentBooking;
            }

            return true;
        }
    },
    methods: {
        getNextAllocation() {
            if (!this.selectedResource || !this.displayRules) {
                return;
            }

            let options = {
                resource_ids: [this.selectedResource],
                skip_current: this.displayRules == 'next',
                limit: 1
            };

            // If the current booking is over, but has a break, skip it
            if (this.nextBooking?.end_with_break > Date.now() && Date.now() > this.nextBooking?.end) {
                options.skip_current = true;
            }

            return this.$api.post('/api/resourcebooking/resource/get_resource_allocation', options).then(async (response) => {
                const bookings = await response.json();
                let booking = bookings?.length ? bookings[0] : null;
                if (booking) {
                    booking.start = Date.newFull(booking.start.$datetime);
                    booking.end = Date.newFull(booking.end.$datetime);
                    booking.end_with_break = Date.newFull(booking.end_with_break.$datetime);

                    this.nextBooking = booking;

                    if (this.isStaff) {
                        let bookerId = booking.booked_by || booking.origin.booker_id;
                        bookerId && this.$api.get('/api/user/get', { id: bookerId }).then((user) => {
                            this.booker = user;
                        });
                        this.fetchActivityName(booking);
                    } else {
                        this.booker = null;
                    }

                    this.setNextBookingTimeout(booking);

                } else {
                    this.nextBooking = null;
                    this.clearBookingTimeout();
                    this.booker = null;
                }

                this.bookingChecked = true;
            });
        },
        setNextBookingTimeout(booking) {
            this.clearBookingTimeout();

            if (!booking) {
                return;
            }

            const now = Date.now();

            const updateAt = [];

            if (['current', 'combined'].includes(this.displayRules)) {
                if (booking.start > now) updateAt.push(booking.start - now); // When booking starts
                if (booking.end > now) updateAt.push(booking.end - now);     // When booking ends
            } else {
                if (booking.start > now) updateAt.push(booking.start - now); // When next booking starts
            }

            updateAt.forEach(timeout => {
                const id = setTimeout(() => {
                    this.getNextAllocation();
                }, timeout);
                // Save all timeouts in an array
                this.nextBookingTimeouts.push(id);
            });
        },
        fetchActivityName(booking) {
            if (booking.origin_type == 'Workout') {
                this.activityName = booking.origin.workoutType.name
            } else if (booking.origin_type == 'ResourceBooking') {
                this.$api.get('/api/resourcebooking/service/get', { id: booking.origin.service_id }).then((service) => {
                    this.activityName = service.name;
                })
            } else {
                this.activityName = 'Unknown';
            }
        },
        clearBookingTimeout() {
            this.nextBookingTimeouts.forEach(id => clearTimeout(id));
            this.nextBookingTimeouts = [];
        },
    },
    created() {
        this.allocationSubscribe = this.$api.subscribe({
            type: 'resourceallocation',
            callback: (booking) => {
                if (booking.resource_id == this.selectedResource) {
                    this.getNextAllocation();
                }
            },
        });
        this.rbSubscribe = this.$api.subscribe({
            type: 'resourcebooking',
            callback: (booking) => {
                if (booking.staff_id == this.selectedResource) {
                    this.getNextAllocation();
                }
            }
        });

        this.nowUpdater = setInterval(() => {
            this.now1s = Date.now();
        }, 1000);
    },
    beforeDestroy() {
        this.allocationSubscribe?.();
        this.rbSubscribe?.();
        this.clearBookingTimeout();
        clearInterval(this.nowUpdater);
    },
};
</script>
